import React, { createContext, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { convertGridToPixels } from "../Pages/FlexibleLayout/utils";
import { tableTypeNameMapping } from "../constant/nameMapping";
import InitialRenderingData, {
  getInitialConfigOfScreen,
  getLayoutDataOfDetailviewElements
} from "../utils/InitialRenderingData";
import { getBlockConfigAndLockdata } from "../utils/blockutils";
import { createChecksum, setModifiedChecksum } from "../utils/checksum";
import { toastError } from "../utils/toastUtils";
import { useAPIContext } from "./APIContext";
import { useAppBlocksContext } from "./AppBlocksContext";
import { useAppOrgContext } from "./AppOrgContext";
import { useAppUserContext } from "./AppUserContext";
import { useCronContext } from "./CronContext";
import { useEntityContext } from "./EntityContext";
import { useFormContext } from "./FormContext";
import { useModalContext } from "./ModalContext";
import { usePublicScreenContext } from "./PublicScreenContext";
import { useScreenContext } from "./ScreenContext";
import { useStatemachineContext } from "./StatemachineContext";
import { useTableContext } from "./TableContext";
import { useTemplateContext } from "./TemplateContext";
import { useTreeContext } from "./TreeDataContext";
import { useWorkflowContext } from "./WorkflowContext";
import { getFolderFileById } from "../utils/treeData/folderEditorUtils";

const NavigationContextApi = createContext();

const useNavigationContext = () => useContext(NavigationContextApi);

const NavigationContext = ({ children }) => {
  const { currentChecksum } = useAppOrgContext();
  const navigate = useNavigate();
  const {
    orgId,
    appId,
    screenId,
    entityId,
    smId,
    blockId,
    formId,
    templateId,
    workflowId,
    cronId,
    apiId,
    publicId,
    rdbmsFormId,
    sqlTemplateId
  } = useParams();
  const {
    getScreenConfig,
    saveScreenJson,
    setLayouts: setScreenLayout,
    layoutData: setScreenLayoutData,
    nestedGridData: screenGridData,
    setCurrentScreenData,
    selectedScreen,
    setLayoutConfig,
    payloadStateList: screenPayloadStateList
  } = useScreenContext();
  const {
    handleSaveEntity,
    getEntityConfig,
    selectedEntity,
    payloadStateList: entityPayloadStateList
  } = useEntityContext();
  const {
    getStatemachineConfig,
    handleSaveStatemachine,
    selectedStateMachine,
    payloadStateList: statemachinePayloadStateList
  } = useStatemachineContext();
  const {
    getAppBlockConfig,
    handleSaveApplicationBlock,
    // setRecordsviewLayout,
    setLayout,
    layoutData,
    nestedGridData,
    selectedAppBlock,
    setCurrentBlockConfig,
    setLockData,
    setApplicationBlockData,
    applicationBlockData,
    // setLayoutConfig: setLayoutConfigForAppBlock,
    payloadStateList: blockPayloadStateList
  } = useAppBlocksContext();
  const {
    getFormConfig,
    handleSaveForm,
    setFormActivityData,
    selectedForm,
    // staticData,
    // setStaticData,
    payloadStateList: formPayloadStateList
  } = useFormContext();
  const { getTemplateConfig, handleSaveTemplate, selectedTemplate, setDesignData } = useTemplateContext();
  const { getWorkflowConfig, handleSaveWorkflow, selectedWorkflow } = useWorkflowContext();
  const { getUserConfirmation } = useModalContext();
  const { getTableTypeData } = useTableContext();
  const { screensExplorerData, formsExplorerData, publicAccessExplorerData, rdbmsFormsExplorerData } = useTreeContext();
  const {
    handleSaveAppUser
    // handleSaveCommunityUser
  } = useAppUserContext();
  const { setCronData, getCronConfig, handleSaveCron, selectedCron } = useCronContext();
  const { getApiConfig, setSelectedApiData, handleSaveApi, selectedApi } = useAPIContext();
  const {
    getAppUserConfig
    // getCommunityUserConfig
  } = useAppUserContext();
  const {
    getPublicAccessConfig,
    savePublicScreenJson,
    setLayouts: setPublicScreenLayout,
    layoutData: setPublicScreenLayoutData,
    nestedGridData: publicScreenGridData,
    setCurrentPublicScreenData,
    selectedPublicScreen
    // currentPublicScreenData
  } = usePublicScreenContext();

  const getInitialRederConfig = async (type, config) => {
    try {
      let tableData = null;
      let tableId = null;
      let screen = null;
      let publicscreen = null;
      const { config: copyConfig, lockData } = getBlockConfigAndLockdata(type, config);
      setLockData(lockData);
      setApplicationBlockData(copyConfig);
      // setInitialChecksum(config);
      // const init = getInitialChecksum()
      const newChecksum = createChecksum(copyConfig);
      currentChecksum.current = newChecksum;

      switch (type) {
        case "recordsview":
        case "pivotcharts":
          tableId = copyConfig.config.layout.table_id;
          break;
        case "detailsview":
        case "activitycard":
          tableId = copyConfig.data.table_id;
          break;
        case "chart":
        case "progress-bar":
          tableId = copyConfig.config.table_id;
          break;
        case "screen":
          screen = screensExplorerData;
          break;
        case "publicscreen":
          publicscreen = publicAccessExplorerData;
          break;
        // case "publicform":
        //   publicform = publicAccessExplorerData;
        //   break;
        case "form":
          const file = getFolderFileById(formsExplorerData, formId);
          tableId = file.parentData.split("/")[0];
          break;
        case "rdbmsform":
          const rdbmsFile = getFolderFileById(rdbmsFormsExplorerData, rdbmsFormId);
          tableId = rdbmsFile.parentData.split("/")[0];
          break;
        case "entity":
          tableId = copyConfig.id;
          break;
        default:
          break;
      }

      if (
        type === "recordsview" ||
        type === "pivotcharts" ||
        type === "detailsview" ||
        type === "chart" ||
        type === "activitycard" ||
        type === "form" ||
        type === "entity" ||
        // type === "progress-bar" ||
        type === "rdbmsform"
      ) {
        let { data, tableObject } = await getTableTypeData(copyConfig.table_type, tableId);
        tableData = { data, tableObject };
      }
      switch (type) {
        case "entity":
          const cartData = getLayoutDataOfDetailviewElements(copyConfig.cart_detailview.elements || [], tableData);
          setLayout(cartData.layout);
          layoutData.current = cartData.layoutData;
          nestedGridData.current = cartData.nestedGridData;
          return null;

        case "recordsview":
          // if (copyConfig.device === "desktop" || copyConfig.device === "tablet") {
          //   const rvDesktopNewConfig = getInitialConfigOfDesktopRecordsview(copyConfig, tableData);
          //   const { catalog } = rvDesktopNewConfig;

          //   setModifiedChecksum(rvDesktopNewConfig.config);

          //   layoutData.current = catalog.layoutData;
          //   nestedGridData.current = catalog.nestedGridData;
          //   setLayout(catalog.layout);
          //   blockDataDispatch({ type: "SET_CONFIG", payload: rvDesktopNewConfig.config });
          //   return rvDesktopNewConfig.config;
          // }
          // if (copyConfig.device === "mobile") {
          //   const m = getInitialConfigOfMobileRecordsview(copyConfig, tableData);
          //   let newPositionedBlockConfig = convertGridToPixels(
          //     copyConfig.position_block_config || [],
          //     copyConfig.position_block_metadata?.grid_size
          //   );
          //   setModifiedChecksum(m.config);
          //   setLayout(m.layout.layout);
          //   layoutData.current = m.layout.layoutData;
          //   nestedGridData.current = m.layout.nestedGridData;
          //   setLayoutConfigForAppBlock(newPositionedBlockConfig);
          //   setRecordsviewLayout(m.config.config.layout);
          // }
          return null;
        case "pivotcharts":
          // if (copyConfig.device === "desktop" || copyConfig.device === "tablet") {
          //   const { newLayout, newLayoutData, newConfig } = InitialRenderingData.getInitialPivotChartConfig(
          //     copyConfig,
          //     tableData
          //   );
          //
          //   setRecordsviewLayout(newConfig);
          //   setLayout(newLayout);
          //   layoutData.current = newLayoutData;
          //
          //   return null;
          // } else {
          //   return null;
          // }
          return null;
        case "detailsview":
          // const dvConf = getInitialConfigOfDetailsView(copyConfig, tableData);
          // setLayout(dvConf.layout.layout);
          // layoutData.current = dvConf.layout.layoutData;
          // nestedGridData.current = dvConf.layout.nestedGridData;
          // // setLayoutConfigForAppBlock(copyConfig.position_block_config || []);
          // if (copyConfig.device === "mobile") {
          //   let newPositionedBlockConfig = convertGridToPixels(
          //     copyConfig.position_block_config || [],
          //     copyConfig.position_block_metadata?.grid_size
          //   );
          //   setLayoutConfigForAppBlock(newPositionedBlockConfig);
          // } else {
          //   setLayoutConfigForAppBlock(copyConfig.position_block_config);
          // }

          // setModifiedChecksum(dvConf.config);

          return null;
        case "activitycard":
          // const ac = getInitialConfigOfActivityCard(copyConfig, tableData);
          // setModifiedChecksum(ac.config);
          // setLayout(ac.layout.layout);
          // layoutData.current = ac.layout.layoutData;
          // nestedGridData.current = ac.layout.nestedGridData;
          // if (copyConfig.device === "mobile") {
          //   let newPositionedBlockConfig = convertGridToPixels(
          //     copyConfig.position_block_config || [],
          //     copyConfig.position_block_metadata?.grid_size
          //   );
          //   setLayoutConfigForAppBlock(newPositionedBlockConfig);
          // } else {
          //   setLayoutConfigForAppBlock(copyConfig.position_block_config);
          // }
          return null;
        case "chart":
          // const cht = getInitialConfigOfChart(copyConfig, tableData);
          // setModifiedChecksum(cht.config);
          // setLayout(cht.layout.layout);
          // layoutData.current = cht.layout.layoutData;
          // const newConfigOfChart = {
          //   global_filters: cht.config.config.global_filters,
          //   tiles: copyConfig.config.tiles,
          //   styles: copyConfig.config.styles
          // };
          // if (copyConfig.device === "mobile") {
          //   let newPositionedBlockConfig = convertGridToPixels(
          //     copyConfig.position_block_config || [],
          //     copyConfig.position_block_metadata?.grid_size
          //   );
          //   setLayoutConfigForAppBlock(newPositionedBlockConfig);
          // } else {
          //   setLayoutConfigForAppBlock(copyConfig.position_block_config);
          // }
          // setChartGlobalFilter(newConfigOfChart);
          return null;
        case "screen":
          const screenNewConfig = getInitialConfigOfScreen(copyConfig, screen);
          const { layout } = screenNewConfig;
          let checksumConfig;
          // const newConfig = InitialRenderingData.getInitialConfigOfScreen(config, screen);
          if (screenNewConfig.config.name === "header") {
            // setHeaderConfig(screenNewConfig.config.header_config);
            // setModuleHeaderConfig(screenNewConfig.config.module_header_config || []);
            // setZadminConfig(screenNewConfig.config.z_admin_config);

            checksumConfig = {
              header_config: screenNewConfig.config.header_config,
              z_admin_config: screenNewConfig.config.z_admin_config,
              module_header_config: screenNewConfig.config.module_header_config
            };
          } else {
            setScreenLayout(layout.layout);
            setScreenLayoutData.current = layout.layoutData;
            screenGridData.current = layout.nestedGridData;
            checksumConfig = screenNewConfig.config.config;
            let newLayout = screenNewConfig.config.position_screen_config;
            if (screenNewConfig.config.device === "mobile") {
              newLayout = convertGridToPixels(
                screenNewConfig.config.position_screen_config || [],
                copyConfig.position_screen_metadata?.grid_size
              );
            }
            setLayoutConfig(newLayout || []);
          }
          setModifiedChecksum(checksumConfig);
          setCurrentScreenData(screenNewConfig.config);
          return null;

        case "publicscreen":
          const newPublicConfig = InitialRenderingData.getInitialConfigOfScreen(copyConfig, publicscreen);
          const {
            layout: publicScreenLayout,
            layoutData: publicScreenLayoutData,
            nestedGridData: publicScreenNestedGridData
          } = newPublicConfig;
          setPublicScreenLayout(publicScreenLayout);
          setPublicScreenLayoutData.current = publicScreenLayoutData;
          publicScreenGridData.current = publicScreenNestedGridData;

          setCurrentPublicScreenData(copyConfig);
          return null;
        case "publicform":
          setCurrentPublicScreenData(copyConfig);
          return null;
        case "cron":
          const { name, expression, function: customFunc } = copyConfig;
          setCronData({ name, expression, function: customFunc });
          return null;
        case "api":
          const obj = {
            path: copyConfig.path,
            method: copyConfig.method,
            variables: copyConfig.variables,
            filters: copyConfig.filters,
            projections: JSON.stringify(copyConfig.projections, null, 2),
            body: JSON.stringify(copyConfig.body, null, 2),
            custom_js: copyConfig.custom_js,
            api_key: copyConfig.api_key,
            app_auth: copyConfig.app_auth
          };
          setSelectedApiData(obj);
          return null;
        case "api-v2":
          const apiV2Obj = {
            url_path: copyConfig.url_path,
            query_params: copyConfig.query_params,
            body_params: copyConfig.body_params,
            path_params: copyConfig.path_params,
            headers: copyConfig.headers,
            job_configs: copyConfig.job_configs,
            api_key: copyConfig.api_key,
            table_type: copyConfig.table_type,
            rdbms_id: copyConfig.rdbms_id,
            sm_id: copyConfig.sm_id,
            api_keys: copyConfig.api_keys,
            zino_auth: copyConfig.zino_auth,
            job_trigger_formula: copyConfig.job_trigger_formula,
            pre_processing: copyConfig.pre_processing,
            post_processing: copyConfig.post_processing,
            version: copyConfig.version,
            success: copyConfig.success,
            error: copyConfig.error,
            method: copyConfig?.method || "post",
            app_id: copyConfig.app_id,
            id: copyConfig.id,
            name: copyConfig.name
          };
          setSelectedApiData(apiV2Obj);
          return null;
        case "rdbmsform":
        case "form":
          const formConfig = { ...copyConfig, custom_functions: copyConfig.custom_functions || [] };
          setFormActivityData(formConfig);
          // setStaticData({
          //   ...staticData,
          //   detailview_id: config.details_view?.detailview_id || "",
          //   detailview_id_for_mobile: config.details_view?.detailview_id_for_mobile || "",
          //   is_positioned_static_data: config.details_view?.is_positioned_static_data ?? false,
          //   position_config: config.details_view?.position_config || []
          // });

          const {
            layout: newData1,
            layoutData: newKanbanData1,
            nestedGridData: newNestedKanbanData1
          } = InitialRenderingData.getInitialConfigOfDetailsView(copyConfig.details_view, tableData);
          setLayout(newData1);
          layoutData.current = newKanbanData1;
          nestedGridData.current = newNestedKanbanData1;
          setCurrentBlockConfig(copyConfig.config);
          return null;
        // case "progress-bar":
        case "queryblocks":
          // setModifiedChecksum(copyConfig);
          // setCurrentBlockConfig(copyConfig.config);
          return null;
        case "sqltemplate":
        case "template":
          const { type } = copyConfig;
          switch (type) {
            case "detailsview":
              const {
                layout,
                image_data,
                layoutData: newLayoutData,
                nestedGridData: newNestedGridData
              } = InitialRenderingData.getInitialConfigOfDetailsView(copyConfig.config, { tableObject: {} });
              setLayout(layout);
              setDesignData((prev) => ({ ...prev, image_data }));
              layoutData.current = newLayoutData;
              nestedGridData.current = newNestedGridData;
              break;

            default:
              break;
          }
          break;
        default:
          return null;
      }
    } catch (error) {
      console.log(error.message, "navigation context");
    }
  };

  const getCurrentPageConfigFunction = async (where) => {
    switch (where) {
      case "screen":
        return getScreenConfig();
      case "entity":
        return getEntityConfig();
      case "statemachine":
        return getStatemachineConfig();
      case "applicationblock":
        return getAppBlockConfig();
      case "form":
      case "rdbmsform":
        return getFormConfig();
      case "template":
      case "sqltemplate":
        return getTemplateConfig();
      case "workflow":
        return await getWorkflowConfig();
      case "cron":
        return getCronConfig();
      case "api":
        return getApiConfig();
      case "appuser":
        return getAppUserConfig();
      // case "communityuser":
      //   return getCommunityUserConfig();
      case "publicaccess":
        return getPublicAccessConfig();
      // case "variables":
      //   return getVariableConfig();
      default:
        break;
    }
  };
  const saveCurrentPageConfig = async () => {
    const { page } = getCurrentPageData();
    switch (page) {
      case "screen":
        await saveScreenJson();
        break;
      case "entity":
        await handleSaveEntity();
        break;
      case "statemachine":
        await handleSaveStatemachine();
        break;
      case "applicationblock":
        await handleSaveApplicationBlock();
        break;
      case "form":
      case "rdbmsform":
        await handleSaveForm();
        break;
      case "sqltemplate":
      case "template":
        await handleSaveTemplate();
        break;
      case "workflow":
        await handleSaveWorkflow();
        break;
      case "cron":
        await handleSaveCron();
        break;
      case "appuser":
        await handleSaveAppUser();
        break;
      // case "communityuser":
      //   await handleSaveCommunityUser();
      // break;
      // case "variables":
      //   await handleSaveVariables();
      //   break;
      case "api":
        await handleSaveApi();
        break;
      case "publicaccess":
        await savePublicScreenJson();
        break;
      default:
        break;
    }
  };
  const getCurrentPageData = () => {
    switch (true) {
      case !!screenId:
        return { id: screenId, page: "screen", info: selectedScreen, stateList: screenPayloadStateList };
      case !!entityId:
        return { id: entityId, page: "entity", info: selectedEntity, stateList: entityPayloadStateList };
      case !!smId:
        return { id: smId, page: "statemachine", info: selectedStateMachine, stateList: statemachinePayloadStateList };
      case !!blockId:
        return { id: blockId, page: "applicationblock", info: selectedAppBlock, stateList: blockPayloadStateList };
      case !!formId:
        return { id: formId, page: "form", info: selectedForm, stateList: formPayloadStateList };
      case !!templateId:
        return { id: templateId, page: "template", info: selectedTemplate };
      case !!workflowId:
        return { id: workflowId, page: "workflow", info: selectedWorkflow };
      case !!cronId:
        return { id: cronId, page: "cron", info: selectedCron };
      case !!apiId:
        return { id: apiId, page: "api", info: selectedApi };
      case !!publicId:
        return { id: publicId, page: "publicaccess", info: selectedPublicScreen };
      case !!rdbmsFormId:
        return { id: rdbmsFormId, page: "rdbmsform", info: selectedForm };
      case !!sqlTemplateId:
        return { id: sqlTemplateId, page: "sqltemplate", info: selectedTemplate };
      default:
        return { id: "", page: "", info: "" };
    }
  };

  const getPageInfoAndPath = async () => {
    const currentPageData = getCurrentPageData();
    if (currentPageData.page !== "applicationblock") return "";
    if (!currentPageData.info.table_id) return;

    // Fetch the necessary data from the second function
    let tableType = applicationBlockData.table_type;
    if (applicationBlockData.data && applicationBlockData.data.activity_id) {
      tableType = "activity";
    }
    const { result, error } = await getSourceAndPath(
      tableType,
      currentPageData.info.table_id,
      currentPageData.info.activity_id
    );

    if (error) return "";
    // Generate the source and path strings here
    let sourceAndPath = {
      source: "",
      path: ""
    };

    if (result) {
      const tableName = tableTypeNameMapping(tableType || "");
      switch (tableType) {
        case "rdbms":
        case "postgres":
          sourceAndPath.source = `Source: ${tableName} / ${result.table_name}`;
          sourceAndPath.path = `../../${appId}/sqltemplate/${currentPageData.info.table_id}`;
          break;

        case "appuser":
          sourceAndPath.source = "Source: Appuser";
          sourceAndPath.path = `../../${appId}/appuser`;
          break;

        // case "communityuser":
        //   sourceAndPath.source = "Source: CommunityUser";
        //   sourceAndPath.path = `../../${appId}/communityuser`;
        //   break;

        case "activity":
          sourceAndPath.source = `Source: statemachine / ${result.table_name} / ${result.activity_name}`;
          sourceAndPath.path = `../../${appId}/statemachine/${currentPageData.info.activity_id}`;
          break;

        case "statemachine":
          sourceAndPath.source = `Source: ${tableName} / ${result.table_name}`;
          sourceAndPath.path = `../../${appId}/statemachine/${currentPageData.info.table_id}`;
          break;

        case "entity":
          sourceAndPath.source = `Source: ${tableName} / ${result.table_name}`;
          sourceAndPath.path = `../../${appId}/entity`;
          break;

        default:
          sourceAndPath.source = "";
          sourceAndPath.path = "";
          break;
      }
    }

    return sourceAndPath;
  };

  const getSourceAndPath = async (tableType, tableId, activityId) => {
    let result = {};
    let error = null;
    try {
      const { data } = await getTableTypeData(tableType, tableId);
      if (!data) {
        error = "No data found";
        return { result, error };
      }

      if (tableType === "rdbms" || tableType === "postgres") {
        result.table_name = data?.table_name;
        if (!result.table_name) {
          error = "Table name not found";
        }
      } else {
        result.table_name = data?.table?.table_name;
        if (!result.table_name) {
          error = "Table name not found";
          return { result, error };
        }
      }

      if (tableType === "activity") {
        const activity = data?.table?.columns_new?.find((act) => act.activity_id === activityId);
        result.activity_name = activity?.name || "";
        if (!result.activity_name) {
          error = "Activity name not found";
        }
      }
    } catch (err) {
      error = `Error fetching data: ${err.message}`;
    }
    return { result, error };
  };

  const getPath = (path, where) => {
    const arrayOfPages = [
      "screen",
      "entity",
      "statemachine",
      "applicationblock",
      "form",
      "template",
      "workflow",
      "cron",
      "api",
      "jobs",
      "appuser",
      // "communityuser",
      "variables",
      "lock",
      "publicaccess",
      "imagerepo",
      "filerepo",
      "chart-editor",
      "rdbmsform",
      "dbconnectors",
      "rdbmsschema",
      "sqltemplate"
    ];

    if (!where) {
      const tempArray = window.location.pathname.split("/");
      where = tempArray[tempArray.length - 1];
      if (!where) {
        where = tempArray[tempArray.length - 2];
      }
    }
    let prePath;
    if (arrayOfPages.includes(path)) {
      prePath = `/organization/${orgId}/app/${appId}/${path}`;
    } else {
      prePath = `/organization/${orgId}/app/${appId}/${where}/${path}`;
    }
    return prePath;
  };

  const saveChangesAndNavigate = async (path) => {
    const { id, page, info } = getCurrentPageData();

    if (!id) {
      navigate(getPath(path, page));
      return;
    }
    // const config = await getCurrentPageConfigFunction(page);
    // if (!config) return;
    // const hasConfigChange = hasChecksumChangedWithModified(config);
    const hasConfigChange = false;
    if (hasConfigChange) {
      // if (newChecksum !== currentChecksum.current) {
      const userConfirmed = await getUserConfirmation(
        `You want to save current <strong style="font-size: 14px;">${info?.name}</strong> change ?`
      );
      if (userConfirmed) {
        try {
          await saveCurrentPageConfig();
          navigate(getPath(path, page));
        } catch (error) {
          toastError("Not saves");
          return;
        }
      } else {
        navigate(getPath(path, page));
      }
    } else {
      navigate(getPath(path, page));
    }
  };

  return (
    <NavigationContextApi.Provider
      value={{
        saveChangesAndNavigate,
        getCurrentPageConfigFunction,
        saveCurrentPageConfig,
        getInitialRederConfig,
        getCurrentPageData,
        getPageInfoAndPath,
        getSourceAndPath
      }}
    >
      {children}
    </NavigationContextApi.Provider>
  );
};

export default NavigationContext;
export { useNavigationContext };
